import { observable, action, makeAutoObservable  } from 'mobx'
import { ReactComponent as Note } from '../svg/note.svg'
import { ReactComponent as Pen } from '../svg/pen.svg'
import { ReactComponent as Text } from '../svg/text.svg'
import { ReactComponent as Bg } from '../svg/background-icon.svg'
import { ReactComponent as Photos } from '../svg/photos.svg'
import { ReactComponent as Uploads } from '../svg/upload-icon.svg'
import { ReactComponent as Objects } from '../svg/objects.svg'

class SidebarItem {
  @observable active
  @observable id
  @observable icon
  @observable subtitle
  @observable title
  @observable pills
  
  constructor(id, icon, title, pills, subtitle, searchable = true, active = false, showInList = true){
    makeAutoObservable(this);
    this.id = id
    this.showInList = showInList
    this.searchable = searchable
    this.icon = icon
    this.title = title
    this.active = active
    this.pills = pills
    this.subtitle = subtitle
  }
}

class SidebarStore {

  @observable workAreaStore

  constructor(workAreaStore) {
    makeAutoObservable(this);
    this.workAreaStore = workAreaStore
  }

  open = false
  activeItem = null
  searchValue = ''
  fullscreenOnly = false
  textStyleOpen = false
  fullScreen = false
  gifsOnly = false
  userPhotosOnly = false
  projectAssetsOnly = false
  teamAssetsOnly = false
  apiPhotosOnly = false
  stickersOnly = false
  objectsOnly = false
  showTextPicker = false
  pageFilters = false
  selectedItem = null
  items = [
    new SidebarItem('templates', <Note />, 'templates', [null, null], null, true),
    new SidebarItem('photos', <Photos />, 'photos', [null, null], null),
    new SidebarItem('gifs', <Objects />, 'gifs', [null, null], null, true),
    new SidebarItem('illustrations', <Pen />, 'illustrations', [null, null], 'Select an illustration', true, false, true),
    new SidebarItem('text', <Text />, 'text', [null, null], 'Text templates', false, false, true),
    new SidebarItem('uploads', <Uploads />, 'uploads', [null, null], 'Your uploads', false, false, true),
    new SidebarItem('color-picker', null, 'Choose a custom color', [null, null], 'Choose a custom fill color', false, false, false),
    new SidebarItem('color-picker-page', null, 'Choose a custom page color', [null, null], 'Choose a custom page color', false, false, false),
    new SidebarItem('svg-color-picker', null, 'Choose a custom fill color', [null, null], 'Choose a custom fill color', false, false, false),
    new SidebarItem('custom-text-style', null, '', [null, null], null, false, false, false),
    new SidebarItem('filters', null, 'Image effects', [null, null], 'Image effects', false, false, false),
    new SidebarItem('background', <Bg />, 'Background', [null, null], 'Choose a page background color', false, false, true)
  ]

  toggleFullscreen(){
    this.fullScreen = !this.fullScreen
  }


  normalizePhoto(item, e){
    const { naturalWidth, naturalHeight } = e.target
    const photo = {
      height: naturalHeight,
      width: naturalWidth,
      urls: {
        regular: item.url
      },
    }
    if(item.parsedSVG){
      photo.parsedSVG = item.parsedSVG
      photo.rawSVG = item.rawSVG
      photo.assetColors = item.assetColors
    }
    if(item.assetId){
      photo.assetId = item.assetId
    }
    return photo
  }


  setSelectedItem(item, type, e){
    if(item && type === 'illustration'){
      this.selectedItem = {...item}
      return
    }
    if(item && item.isUpload && type === 'photo'){
      this.selectedItem = {...this.normalizePhoto(item, e), isUpload: true, type}
      return
    }
    if(item && type === 'gif'){
      this.selectedItem = {...this.standardizeGif(item), isUpload: true, type: 'photo'}
      return
    }
    this.selectedItem = item
  }

  standardizeGif(gif){
    const { originalWidth: ow, originalHeight: oh, frames: f, id, type, photoType, url } = gif
    const w = Number(ow)
    const h = Number(oh)
    const frames = Number(f)
    return {
      urls: {
        regular: url
      },
      height: h,
      width: w,
      xPos: 0,
      yPos: 0,
      scale: 1,
      xCrop: 0,
      yCrop: 0,
      frames,
      groupId: null,
      photoType
    }
  }

  insertDrag(clientX, clientY){
    this.workAreaStore.insertItemDrag(this.selectedItem, clientX, clientY)
    this.selectedItem = null
  }

  changeStatus(itemID){
    if(itemID !== 'svg-color-picker'){
      this.workAreaStore.setEditingColor(null)
    }
    if(!this.workAreaStore.compiled){
      return
    }
    this.showTextPicker = false
    this.textStyleOpen = false
    if(this.workAreaStore.resizingPage){
      this.workAreaStore.toggleBackgroundCropper(this.workAreaStore.resizingPage, true)
    }
    this.fullScreen = false
    this.searchValue = ''
    this.unsetObjectsView()
    this.items.forEach(item => {
      if(item.id === itemID){
        item.active = true
        this.activeItem = item
        this.open = true
        if(itemID === 'custom-text-style'){
          this.open = false
          this.textStyleOpen = true
        }
      }else{
        item.active = false
      }
      if(itemID !== 'page-filters'){
        this.pageFilters = false
      }
      if(item.id === 'filters' && itemID === 'page-filters'){
        this.pageFilters = true
        this.open = true
        this.activeItem = item
        item.active = true
      }
    })
  }

  changeSearchValue(val){
    this.searchValue = val
  }

  unsetObjectsView(){
    this.stickersOnly = false
    this.gifsOnly = false
    this.userPhotosOnly = false
    this.apiPhotosOnly = false
    this.objectsOnly = false
    this.projectAssetsOnly = false
    this.teamAssetsOnly = false
  }

  setObjectsView(type){
    this.unsetObjectsView()
    switch(type){
      case 'gifs':
        this.gifsOnly = true
        break
      case 'stickers':
        this.stickersOnly = true
        break
      case 'brand-assets':
        this.projectAssetsOnly = true
        break
      case 'team-assets':
        this.teamAssetsOnly = true
        break
      case 'user-photos':
        this.userPhotosOnly = true
        break
      case 'api-photos':
        this.apiPhotosOnly = true
        break
      default:
        this.objectsOnly = true
        break
    }
  }

  closeSidebarSlide(){
    this.workAreaStore.setEditingColor(null)
    this.unsetObjectsView()
    this.open = false
    this.fullScreen = false
    this.showTextPicker = false
    this.textStyleOpen = false
    this.items.forEach(item => {
      item.active = false
    })
    this.activeItem = null
  }

  getActiveSlide(){
    return this.activeItem
  }

  setTextColorPicker(status){
    this.showTextPicker = status
  }

  setTextStyleSlide(status){
    this.textStyleOpen = status
    this.fullScreen = false
  }
}

export default SidebarStore